import { translateBackend } from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';
import { OpenInNew, type SvgIconComponent } from '@mui/icons-material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Icon as MuiIcon,
} from '@mui/material';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { Link } from '@ovComponents/2-component/orgLink/orgLink';
import { BetaBadge } from '@ovComponents/3-pattern/betaBadge/betaBadge';
import { UserContext } from '@providers/userContextProvider';
import { Box, Typography, useTheme } from 'hephaestus/web';
import { kebabCase } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../badge/badge';
import { useNavSectionTokens } from './navSection.tokens';

export type SubSection = {
  name: string | TranslatedString;
  path: string;
  accessKey?: string;
  permissions?: string[];
  configurableTablePage?: boolean;
  beta?: boolean;
  newTab?: boolean;
  /** When true, only highlight on an exact path match (not nested routes). */
  matchPathExactly?: boolean;
  stats?:
    | 'flagsCount'
    | 'subAccountsToReviewCount'
    | 'complianceReviewCount'
    | 'alertsCount'
    | 'statementsToReviewCount'
    | 'annualSuitabilityReviewCount'
    | 'bankAccountAwaitingReviewCount'
    | 'institutionAwaitingReviewCount';
};

export type Section = {
  name: string | TranslatedString;
  icon: SvgIconComponent | string;
  subSections: SubSection[];
  beta?: boolean;
  newTab?: boolean;
  /** Keep parent/child nav labels when there is only one sub-section. */
  forceExpandable?: boolean;
};

export const getSubSectionName = (subSectionName: SubSection['name']) =>
  typeof subSectionName === 'string'
    ? subSectionName
    : translateBackend(subSectionName);

export const isNavSubSectionPathActive = (
  sub: Pick<SubSection, 'path' | 'matchPathExactly'>,
  pathname: string,
): boolean => {
  if (sub.path === '/') {
    return pathname === '/';
  }
  if (sub.matchPathExactly) {
    return pathname === sub.path;
  }
  return pathname === sub.path || pathname.startsWith(`${sub.path}/`);
};

export const NavSection = ({
  section,
  accessiblePages,
  handleDrawerClick,
  globalStats,
  displayStats,
  showSection,
  setSectionsOpen,
  sectionsOpen,
  index,
  location,
  closed,
  openedMenu,
}: {
  section: Section;
  accessiblePages: Set<string>;
  handleDrawerClick: (x: string) => void;
  globalStats: any;
  displayStats: (x: SubSection) => boolean;
  showSection: boolean;
  setSectionsOpen: (x: boolean[]) => void;
  openedMenu: boolean;
  sectionsOpen: boolean[];
  index: number;
  location: any;
  closed: boolean;
}) => {
  const { t } = useTranslation(['navMenu']);
  const { permissions } = useContext(UserContext);
  const pageIsAccessible = (
    key?: string,
    permissionsRequired?: string[],
  ): boolean =>
    (!key && !permissionsRequired) ||
    (!!key && accessiblePages.has(key)) ||
    (permissionsRequired?.some((x) => permissions.has(x)) ?? false);
  const theme = useTheme();
  const tokens = useNavSectionTokens(theme);
  const [localClosed, setLocalClosed] = useState<boolean>(closed);

  useEffect(() => {
    if (closed) {
      setLocalClosed(closed);
    } else {
      setTimeout(() => {
        setLocalClosed(closed);
      }, 150);
    }
  }, [closed]);

  const buttonStyle = {
    padding: '12px 12px',
    color: tokens.color,
    borderRadius: tokens.radius,
    '&:hover,&.Mui-selected': {
      backgroundColor: tokens.selectedColor,
    },
    '&:hover&.Mui-selected': { backgroundColor: tokens.selectedColor },
    '&:hover': { backgroundColor: tokens.hoverColor },
  };

  const sectionActive = () =>
    section.subSections
      .filter(
        (x: SubSection) =>
          !x.accessKey || pageIsAccessible(x.accessKey, x.permissions),
      )
      .some((x: SubSection) => isNavSubSectionPathActive(x, location.pathname));

  const menuPopout = () => (
    <Box
      sx={{
        position: 'absolute',
        top: '0px',
        left: '100%',
        display: {
          xs: 'none',
          lg: openedMenu && closed ? 'block' : 'none',
        },
        background: tokens.backgroundColor,
        p: '4px',
        borderRadius: '8px',
        boxShadow: theme.elevation.high,
        minWidth: '200px',
      }}
    >
      {section.subSections
        .filter((y: SubSection) => pageIsAccessible(y.accessKey, y.permissions))
        .map((sub: SubSection) => (
          <ListItem
            disablePadding
            key={getSubSectionName(sub.name)}
            component={Link}
            to={sub.path}
            target={sub.newTab ? '_blank' : '_self'}
            data-testid={kebabCase(getSubSectionName(sub.name) ?? '')}
            onClick={() => handleDrawerClick(getSubSectionName(sub.name) ?? '')}
            secondaryAction={
              displayStats(sub) && (
                <Badge
                  label={
                    globalStats[sub.stats!] > 999
                      ? '999+'
                      : globalStats[sub.stats!].toString()
                  }
                  color='negative'
                  variant='number'
                />
              )
            }
          >
            <ListItemButton
              sx={{ ...buttonStyle }}
              selected={isNavSubSectionPathActive(sub, location.pathname)}
            >
              <Typography
                variant='bodySmall'
                sx={{
                  fontWeight: isNavSubSectionPathActive(sub, location.pathname)
                    ? 500
                    : undefined,
                }}
              >
                {typeof sub.name === 'string'
                  ? t(sub.name)
                  : translateBackend(sub.name)}
              </Typography>
            </ListItemButton>
          </ListItem>
        ))}
    </Box>
  );

  const subSections = section.subSections.filter((x: SubSection) =>
    pageIsAccessible(x.accessKey, x.permissions),
  );
  if (subSections.length === 0) {
    return <></>;
  }
  if (subSections.length === 1 && !section.forceExpandable) {
    const s0 = subSections[0];
    const title = showSection
      ? getSubSectionName(section.name)
      : getSubSectionName(s0.name);
    return (
      <Box
        key={getSubSectionName(section.name)}
        sx={{
          pl: 0.5,
          pr: 0.5,
          position: 'relative',
          mb: 0.25,
        }}
      >
        <ListItem
          key={
            typeof s0.name === 'string' ? (t(s0.name) as string) : s0.name.en
          }
          component={Link}
          to={s0.path}
          target={s0.newTab || section.newTab ? '_blank' : '_self'}
          data-testid={kebabCase(getSubSectionName(s0.name) ?? '')}
          onClick={() => handleDrawerClick(getSubSectionName(s0.name) ?? '')}
          disablePadding
          secondaryAction={
            displayStats(s0) && (
              <Badge
                label={
                  globalStats[s0.stats!] > 999
                    ? '999+'
                    : globalStats[s0.stats!].toString()
                }
                color='negative'
                variant='number'
              />
            )
          }
        >
          <ListItemButton
            selected={isNavSubSectionPathActive(s0, location.pathname)}
            sx={buttonStyle}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'start',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                {typeof section.icon === 'string' ? (
                  <MuiIcon
                    sx={{
                      color: tokens.color,
                      fontSize: '16px !important',
                      p: '2px',
                    }}
                  >
                    {section.icon}
                  </MuiIcon>
                ) : (
                  <Icon
                    icon={section.icon as SvgIconComponent}
                    sx={{ color: tokens.color, p: '2px' }}
                    size='small'
                  />
                )}
              </ListItemIcon>
              {!localClosed && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
                    {typeof title === 'string'
                      ? t(title)
                      : translateBackend(title)}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'end',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    {section.beta && (
                      <BetaBadge sx={{ ml: 1 }} variant='small' />
                    )}
                    {section.newTab && <OpenInNew sx={{ fontSize: '16px' }} />}
                  </Box>
                </Box>
              )}
            </Box>
          </ListItemButton>
        </ListItem>
        {menuPopout()}
      </Box>
    );
  }
  return (
    <Box
      key={getSubSectionName(section.name)}
      sx={{
        pl: 0.5,
        pr: 0.5,
        position: 'relative',
        overflow: 'visible',
        mb: 0.25,
      }}
    >
      <ListItemButton
        onClick={() =>
          setSectionsOpen(
            sectionsOpen.map((val, idx) => (idx === index ? !val : val)),
          )
        }
        sx={buttonStyle}
        data-testid={`${kebabCase(getSubSectionName(section.name) ?? '')}-nav-section`}
        selected={sectionActive() && localClosed}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'start',
              alignItems: 'center',
            }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {typeof section.icon === 'string' ? (
                <MuiIcon
                  sx={{
                    color: tokens.color,
                    fontSize: '16px !important',
                    p: '2px',
                  }}
                >
                  {section.icon}
                </MuiIcon>
              ) : (
                <Icon
                  icon={section.icon as SvgIconComponent}
                  sx={{ color: tokens.color, p: '2px' }}
                  size='small'
                />
              )}
            </ListItemIcon>
            {!localClosed && (
              <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
                {typeof section.name === 'string'
                  ? t(section.name)
                  : translateBackend(section.name)}
              </Typography>
            )}
          </Box>
          {section.newTab ? (
            <OpenInNew sx={{ fontSize: '16px' }} />
          ) : (
            !localClosed &&
            (sectionsOpen[index] ? (
              <ExpandLess sx={{ fontSize: '16px' }} />
            ) : (
              <ExpandMore sx={{ fontSize: '16px' }} />
            ))
          )}
        </Box>
      </ListItemButton>
      <Collapse
        in={sectionsOpen[index] && !localClosed}
        timeout='auto'
        unmountOnExit
      >
        <List component='div' sx={{ padding: 0 }}>
          {section.subSections
            .filter((y: SubSection) =>
              pageIsAccessible(y.accessKey, y.permissions),
            )
            .map((sub: SubSection) => (
              <ListItem
                sx={{ mb: 0.25 }}
                disablePadding
                key={getSubSectionName(sub.name)}
                component={Link}
                to={sub.path}
                target={sub.newTab ? '_blank' : '_self'}
                data-testid={kebabCase(getSubSectionName(sub.name) ?? '')}
                onClick={() =>
                  handleDrawerClick(getSubSectionName(sub.name) ?? '')
                }
                secondaryAction={
                  displayStats(sub) && (
                    <Badge
                      label={
                        globalStats[sub.stats!] > 999
                          ? '999+'
                          : globalStats[sub.stats!].toString()
                      }
                      color='negative'
                      variant='number'
                    />
                  )
                }
              >
                <ListItemButton
                  sx={{ ...buttonStyle }}
                  selected={isNavSubSectionPathActive(sub, location.pathname)}
                >
                  <Typography
                    variant='bodySmall'
                    sx={{
                      fontWeight: isNavSubSectionPathActive(
                        sub,
                        location.pathname,
                      )
                        ? 500
                        : undefined,
                    }}
                  >
                    {typeof sub.name === 'string'
                      ? t(sub.name)
                      : translateBackend(sub.name)}
                  </Typography>
                  {sub.beta && <BetaBadge sx={{ ml: 1 }} variant='small' />}
                </ListItemButton>
              </ListItem>
            ))}
        </List>
      </Collapse>
      {menuPopout()}
    </Box>
  );
};
